import { useCallback, useMemo } from 'react';
import useMapAsState from 'use-map-as-state';
import useSetAsState from 'use-set-as-state';
import useReactQueryMutationOptions from './useReactQueryMutationOptions';
export var OptimistUpdaterResult;
(function (OptimistUpdaterResult) {
    OptimistUpdaterResult["NotFound"] = "NotFound";
    OptimistUpdaterResult["Success"] = "Success";
    OptimistUpdaterResult["Error"] = "Error";
})(OptimistUpdaterResult || (OptimistUpdaterResult = {}));
/**
 * Optimist helps make optimistic queryCache updates easy.
 * Register @see OptimistUpdater in order to immediately render changes for your useMutations, while you wait for the data to be refetched.
 *
 * @returns The exact options object useMutation requires. You can control 100% of these options using Optimist, at either an updater or global level.
 *
 * @param optimistOptions These are various global options to control how Optimist responds, @see OptimistOptions . If you receive a type error on one of the globalMutationOptions, try making it async (even if for no reason).
 */
const useOptimist = (optimistOptions) => {
    const registeredUpdatersBySerializedQueryKey = useMapAsState(new Map());
    const unregisteredSerializedQueryKeys = useSetAsState(new Set());
    /** This splitting may seem unnecessary and expensive, but it absolutely prevents infinite re-renders with no extra effort on the consumer side (and is why we memo). */
    const updaters = useMemo(() => [...registeredUpdatersBySerializedQueryKey.entries()].reduce((result, [serializedQueryKey, updater]) => {
        if (!unregisteredSerializedQueryKeys.has(serializedQueryKey)) {
            return [...result, updater];
        }
        return result;
    }, []), [registeredUpdatersBySerializedQueryKey, unregisteredSerializedQueryKeys]);
    /**
     * Register an updater with Optimist.
     * If you have deep concerns on massive numbers of renders, feel free to wrap this with a useEffect.
     * @see TPrevious is the type associated with the previous query response in the query cache (and what you return from update callback).
     * You should always provide a generic to this function, and you can cast if you are using a partial object in your optimistic render.
     *
     * @param updateCallback Please treat @see TPrevious as immutable. We attempt to clone for you, but this doesn't always behave as expected, and you should still apply React best practices.
     */
    const registerUpdater = useCallback((queryKey, updateCallback, options) => {
        const serializedQueryKey = JSON.stringify(queryKey);
        if (!registeredUpdatersBySerializedQueryKey.has(serializedQueryKey)) {
            registeredUpdatersBySerializedQueryKey.set(serializedQueryKey, { queryKey, updateCallback, options });
        }
    }, [registeredUpdatersBySerializedQueryKey]);
    /**
     * Remove an updater from Optimist's task list.
     * This is often useful when you are conditionally registering based on types, and you should unregister the other types to be sure they aren't firing.
     */
    const unregisterUpdater = useCallback((queryKey) => {
        const serializedQueryKey = JSON.stringify(queryKey);
        if (!unregisteredSerializedQueryKeys.has(serializedQueryKey)) {
            unregisteredSerializedQueryKeys.add(serializedQueryKey);
        }
    }, [unregisteredSerializedQueryKeys]);
    /**
     * This should be handed to useMutation directly.
     */
    const mutationOptions = useReactQueryMutationOptions(updaters, optimistOptions);
    return { registerUpdater, unregisterUpdater, mutationOptions };
};
export default useOptimist;
//# sourceMappingURL=index.js.map